import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../api';
import './Addendums.css';
import { currencyFormatter } from '../valueformatter';

const TAXING_AUTHORITIES = [
    'IRS',
    'STATE',
    'IRS/STATE',
];

const EMPTY_FORM = {
    taxingAuthority: '',
    caseNumber: '',
    clientName: '',
    invoiceAmount: '',
    accountNumber: '',
    paymentDate: '',
    throughTaxYear: '',
    poaStartDate: '',
    poaEndDate: '',
    debtAmount: '',
    services: [],
};

export default function Addendums() {
    const [isOpen, setIsOpen] = useState(true);
    const [form, setForm] = useState(EMPTY_FORM);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);

    // Service catalog + contract-header text, pulled from Postgres via our own API.
    const [serviceOptions, setServiceOptions] = useState([]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    // Format currency fields to USD on blur, strip formatting when focused back
    const handleCurrencyBlur = (e) => {
        const { name, value } = e.target;
        const num = parseFloat(value.replace(/[^0-9.]/g, ''));
        if (!isNaN(num)) {
            setForm(prev => ({ ...prev, [name]: currencyFormatter(num, '$') }));
        }
    };

    const handleCurrencyFocus = (e) => {
        const { name, value } = e.target;
        const stripped = value.replace(/[^0-9.]/g, '');
        setForm(prev => ({ ...prev, [name]: stripped }));
    };

    // Add a service to the array (called when the dropdown changes)
    const addService = (e) => {
        const serviceId = e.target.value;
        if (!serviceId) return;
        if (form.services.some(s => String(s.id) === String(serviceId))) {
            e.target.value = '';
            return;
        }
        const service = serviceOptions.find(s => String(s.id) === String(serviceId));
        if (!service) return;
        setForm(prev => ({
            ...prev,
            services: [...prev.services, {
                id: String(serviceId),
                name: service.name ?? '',
                header: service.header ?? '',
                headerDescription: service.header_description ?? '',
                subHeader: service.sub_header ?? '',
                programs: service.programs ?? '',
                programDescription: service.program_description ?? '',
                caseResolutionStatements: service.case_resolution_statements ?? '',
                notes: '',
            }],
        }));
        e.target.value = '';
    };

    const updateServiceField = (index, field, value) => {
        setForm(prev => ({
            ...prev,
            services: prev.services.map((s, i) => i === index ? { ...s, [field]: value } : s),
        }));
    };

    const removeService = (index) => {
        setForm(prev => ({
            ...prev,
            services: prev.services.filter((_, i) => i !== index),
        }));
    };

    const handleReset = () => {
        setForm(EMPTY_FORM);
        setError(null);
    };

    const handleGenerate = async () => {
        setError(null);
        const required = ['taxingAuthority', 'caseNumber', 'clientName', 'invoiceAmount'];
        const missing = required.filter(k => !form[k]);
        if (missing.length) {
            setError(`Please fill in: ${missing.join(', ')}`);
            return;
        }
        if (form.services.length === 0) {
            setError('Please select at least one service.');
            return;
        }

        const parseAmount = v => parseFloat(String(v).replace(/[^0-9.]/g, '')) || 0;
        const submission = {
            ...form,
            invoiceAmount: currencyFormatter(parseAmount(form.invoiceAmount), '$'),
            debtAmount: currencyFormatter(parseAmount(form.debtAmount), '$'),
        };

        setGenerating(true);
        try {
            const res = await apiFetch('/api/addendum/generate', {
                method: 'POST',
                body: JSON.stringify(submission),
            });

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || 'Server error');
            }

            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Addendum-${form.caseNumber}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(err.message);
        } finally {
            setGenerating(false);
        }
    };

    useEffect(() => {
        const fetchServiceOptions = async () => {
            try {
                const res = await apiFetch('/api/services');
                if (!res.ok) throw new Error(await res.text());
                const data = await res.json();
                setServiceOptions(data || []);
            } catch (err) {
                setError('Unable to load service catalog: ' + err.message);
            }
        };
        fetchServiceOptions();
    }, []);

    return (
        <div className='addendum-page'>
            {/* ── Collapsible Configuration Section ── */}
            <div className='addendum-panel'>
                <button
                    className='addendum-panel__header'
                    onClick={() => setIsOpen(prev => !prev)}
                    aria-expanded={isOpen}
                >
                    <span>Addendum Configuration</span>
                    <span className={`addendum-panel__chevron ${isOpen ? 'addendum-panel__chevron--open' : ''}`}>▼</span>
                </button>

                {isOpen && (
                    <div className='addendum-panel__body'>
                        {/* Row 1 – Taxing Authority */}
                        <div className='addendum-row'>
                            <div className='addendum-field'>
                                <label className='addendum-label'>Taxing Authority</label>
                                <select
                                    name='taxingAuthority'
                                    value={form.taxingAuthority}
                                    onChange={handleChange}
                                    className='addendum-select'
                                >
                                    <option value=''>— Select Authority —</option>
                                    {TAXING_AUTHORITIES.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Row 2 – Case & Client */}
                        <div className='addendum-row'>
                            <div className='addendum-field'>
                                <label className='addendum-label'>Case Number</label>
                                <input
                                    type='text'
                                    name='caseNumber'
                                    value={form.caseNumber}
                                    onChange={handleChange}
                                    placeholder='e.g. 12345'
                                    className='addendum-input'
                                />
                            </div>
                            <div className='addendum-field'>
                                <label className='addendum-label'>Client Name</label>
                                <input
                                    type='text'
                                    name='clientName'
                                    value={form.clientName}
                                    onChange={handleChange}
                                    placeholder='Full legal name'
                                    className='addendum-input'
                                />
                            </div>
                        </div>

                        {/* Row 3 – Financials */}
                        <div className='addendum-row'>
                            <div className='addendum-field'>
                                <label className='addendum-label'>Invoice Amount</label>
                                <input
                                    type='text'
                                    inputMode='decimal'
                                    name='invoiceAmount'
                                    value={form.invoiceAmount}
                                    onChange={handleChange}
                                    onBlur={handleCurrencyBlur}
                                    onFocus={handleCurrencyFocus}
                                    placeholder='$0.00'
                                    className='addendum-input'
                                />
                            </div>
                            <div className='addendum-field'>
                                <label className='addendum-label'>Debt Amount</label>
                                <input
                                    type='text'
                                    inputMode='decimal'
                                    name='debtAmount'
                                    value={form.debtAmount}
                                    onChange={handleChange}
                                    onBlur={handleCurrencyBlur}
                                    onFocus={handleCurrencyFocus}
                                    placeholder='$0.00'
                                    className='addendum-input'
                                />
                            </div>
                            <div className='addendum-field'>
                                <label className='addendum-label'>Account Number</label>
                                <input
                                    type='text'
                                    name='accountNumber'
                                    value={form.accountNumber}
                                    onChange={handleChange}
                                    placeholder='Payment account #'
                                    className='addendum-input'
                                />
                            </div>
                            <div className='addendum-field'>
                                <label className='addendum-label'>Payment Date</label>
                                <input
                                    type='date'
                                    name='paymentDate'
                                    value={form.paymentDate}
                                    onChange={handleChange}
                                    className='addendum-input'
                                />
                            </div>
                        </div>

                        {/* Row 4 – Dates */}
                        <div className='addendum-row'>
                            <div className='addendum-field'>
                                <label className='addendum-label'>Through Tax Year</label>
                                <input
                                    type='number'
                                    name='throughTaxYear'
                                    value={form.throughTaxYear}
                                    onChange={handleChange}
                                    placeholder='e.g. 2023'
                                    className='addendum-input'
                                />
                            </div>
                            <div className='addendum-field'>
                                <label className='addendum-label'>POA Start Date</label>
                                <input
                                    type='date'
                                    name='poaStartDate'
                                    value={form.poaStartDate}
                                    onChange={handleChange}
                                    className='addendum-input'
                                />
                            </div>
                            <div className='addendum-field'>
                                <label className='addendum-label'>POA End Date</label>
                                <input
                                    type='date'
                                    name='poaEndDate'
                                    value={form.poaEndDate}
                                    onChange={handleChange}
                                    className='addendum-input'
                                />
                            </div>
                        </div>

                        {/* Services – multi-select */}
                        <div className='addendum-row'>
                            <div className='addendum-field' style={{ flex: '1 1 100%' }}>
                                <label className='addendum-label'>Add Service <span style={{ fontWeight: 400, color: '#888' }}>(select to add; each service generates its own page)</span></label>
                                <select
                                    value=''
                                    onChange={addService}
                                    className='addendum-select'
                                >
                                    <option value=''>— Select a service to add —</option>
                                    {serviceOptions
                                        .filter(s => !form.services.some(sel => String(sel.id) === String(s.id)))
                                        .map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Per-service editable cards */}
                        {form.services.map((svc, index) => (
                            <div key={svc.id} className='addendum-service-card'>
                                <div className='addendum-service-card__header'>
                                    <span className='addendum-service-card__title'>
                                        Service {index + 1}: {svc.name || '—'}
                                    </span>
                                    <button
                                        type='button'
                                        className='redButton addendum-service-card__remove'
                                        onClick={() => removeService(index)}
                                        disabled={generating}
                                    >
                                        Remove
                                    </button>
                                </div>

                                <div className='addendum-row'>
                                    <div className='addendum-field'>
                                        <label className='addendum-label'>Header</label>
                                        <input
                                            type='text'
                                            value={svc.header}
                                            onChange={e => updateServiceField(index, 'header', e.target.value)}
                                            placeholder='Main header text at top of addendum'
                                            className='addendum-input'
                                        />
                                    </div>
                                </div>

                                <div className='addendum-row'>
                                    <div className='addendum-field'>
                                        <label className='addendum-label'>Header Description</label>
                                        <input
                                            type='text'
                                            value={svc.headerDescription}
                                            onChange={e => updateServiceField(index, 'headerDescription', e.target.value)}
                                            placeholder='Smaller text following header (optional)'
                                            className='addendum-input'
                                        />
                                    </div>
                                </div>

                                <div className='addendum-row'>
                                    <div className='addendum-field'>
                                        <label className='addendum-label'>Sub-header</label>
                                        <input
                                            type='text'
                                            value={svc.subHeader}
                                            onChange={e => updateServiceField(index, 'subHeader', e.target.value)}
                                            placeholder='Text directly beneath header description'
                                            className='addendum-input'
                                        />
                                    </div>
                                </div>

                                <div className='addendum-row'>
                                    <div className='addendum-field'>
                                        <label className='addendum-label'>Programs</label>
                                        <input
                                            type='text'
                                            value={svc.programs}
                                            onChange={e => updateServiceField(index, 'programs', e.target.value)}
                                            placeholder='List of programs (optional)'
                                            className='addendum-input'
                                        />
                                    </div>
                                </div>

                                <div className='addendum-row'>
                                    <div className='addendum-field'>
                                        <label className='addendum-label'>Program Description <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional – overrides default)</span></label>
                                        <textarea
                                            value={svc.programDescription}
                                            onChange={e => updateServiceField(index, 'programDescription', e.target.value)}
                                            placeholder=''
                                            className='addendum-input addendum-textarea'
                                            rows={2}
                                        />
                                    </div>
                                </div>

                                <div className='addendum-row'>
                                    <div className='addendum-field'>
                                        <label className='addendum-label'>Case Resolution Statements <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional – overrides default)</span></label>
                                        <textarea
                                            value={svc.caseResolutionStatements}
                                            onChange={e => updateServiceField(index, 'caseResolutionStatements', e.target.value)}
                                            placeholder=''
                                            className='addendum-input addendum-textarea'
                                            rows={6}
                                        />
                                    </div>
                                </div>

                                <div className='addendum-row'>
                                    <div className='addendum-field'>
                                        <label className='addendum-label'>Notes <span style={{ fontWeight: 400, textTransform: 'none', color: '#888' }}>(optional)</span></label>
                                        <textarea
                                            value={svc.notes}
                                            onChange={e => updateServiceField(index, 'notes', e.target.value)}
                                            placeholder="Additional notes to appear on this service's page…"
                                            className='addendum-input addendum-textarea'
                                            rows={3}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}

                        {/* Actions */}
                        <div className='addendum-actions'>
                            <button className='blueButton' onClick={handleGenerate} disabled={generating}>
                                {generating ? 'Generating…' : 'Generate PDF'}
                            </button>
                            <button className='redButton' onClick={handleReset} disabled={generating}>Reset</button>
                        </div>
                        {error && <p className='addendum-error'>{error}</p>}
                    </div>
                )}
            </div>
        </div>
    );
}
